import config from 'app-config'
import { constants } from 'common/helpers'
import { useEffect, useCallback, useScriptsLoader, useReducerState } from 'sb-hooks'


type AmazonOrderType = {
  paymentMethod: string
  accessToken: string
  orderReferenceId: string
}

type OptionsType = {
  amazonPayButtonId: string
  setAmazonOrderData: (amazonOrderData: AmazonOrderType) => void
}

type ReturnType = {
  accessToken: string
  orderReferenceId: string
  isAmazonButtonLoading: boolean
}

const testtUrl  = 'https://static-na.payments-amazon.com/OffAmazonPayments/us/sandbox/js/Widgets.js'
const prodUrl   = 'https://static-na.payments-amazon.com/OffAmazonPayments/us/js/Widgets.js'
const scriptUrl = config.runEnv === 'prod' ? prodUrl : testtUrl


const useAmazonPayButton = ({ amazonPayButtonId, setAmazonOrderData }: OptionsType): ReturnType => {
  useScriptsLoader([ scriptUrl ])

  const [ state, setState ] = useReducerState({
    isAmazonButtonLoading: true,
    accessToken: null,
    orderReferenceId: null,
  })

  const { isAmazonButtonLoading, accessToken, orderReferenceId } = state

  useEffect(() => {
    if (accessToken && orderReferenceId) {
      setAmazonOrderData({
        accessToken,
        orderReferenceId,
        paymentMethod: constants.paymentMethods.amazonPay,
      })
    }
  }, [ accessToken, orderReferenceId, setAmazonOrderData ])

  const showButton = useCallback(() => {
    window.OffAmazonPayments.Button(amazonPayButtonId, config.publicKeys.amazonPaySellerId, {
      type: 'PwA',
      authorization: () => {
        const loginOptions = {
          scope: 'profile payments:widget payments:shipping_address',
          popup: true,
        }

        window.amazon.Login.authorize(loginOptions, (authorizeData) => {
          const { access_token, error } = authorizeData

          if (error) {
            console.error(error)
          }
          else {
            setState({ accessToken: access_token })
          }
        })
      },
      onSignIn: (orderReference) => {
        const orderReferenceId = orderReference.getAmazonOrderReferenceId()

        setState({ orderReferenceId })
      },
      onError: (error) => console.error(error),
    })

    setState({ isAmazonButtonLoading: false })
  }, [ amazonPayButtonId ])

  const initAmazon = useCallback(() => {
    window.amazon.Login.setClientId(config.publicKeys.amazonPayClientId)
    window.amazon.Login.setUseCookie(true)
  }, [])

  useEffect(() => {
    if (window.OffAmazonPayments) {
      showButton()
    }
    else {
      window.onAmazonLoginReady = () => initAmazon()
      window.onAmazonPaymentsReady = () => showButton()
    }
  }, [ initAmazon, showButton ])

  return {
    accessToken,
    orderReferenceId,
    isAmazonButtonLoading,
  }
}


export default useAmazonPayButton
